import { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Crown, Check, Sparkles, Shield, Users, Filter, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Premium = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [packagesRes, statusRes] = await Promise.all([
        axios.get(`${API}/premium/packages`),
        axios.get(`${API}/premium/status`)
      ]);
      setPackages(packagesRes.data);
      setPremiumStatus(statusRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load premium information');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your profile.');
      setPollingStatus(false);
      return;
    }

    setPollingStatus(true);

    try {
      const response = await axios.get(`${API}/premium/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid' || response.data.status === 'completed') {
        toast.success('🎉 Premium activated! Welcome to Bar Buzz Premium!');
        setPollingStatus(false);
        
        // Refresh user data
        const meRes = await axios.get(`${API}/auth/me`);
        setUser(meRes.data);
        setPremiumStatus({ is_premium: true, premium_expires_at: response.data.premium_expires_at });
        
        // Clear URL params
        navigate('/premium', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPollingStatus(false);
        navigate('/premium', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error('Error checking payment status.');
        setPollingStatus(false);
      }
    }
  };

  const handleCheckout = async (packageId) => {
    setCheckingOut(packageId);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/premium/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show processing state if polling
  if (pollingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Card className="glass p-8 border border-purple-500/50 text-center max-w-md">
          <Loader2 className="w-16 h-16 text-purple-400 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-white mb-2">Processing Payment...</h2>
          <p className="text-slate-400">Please wait while we confirm your payment.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Crown className="w-6 h-6 text-yellow-400" />
            Premium
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12 fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-semibold">Unlock Premium Features</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Upgrade Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">Bar Experience</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Get exclusive access to advanced features, ad-free browsing, and insider perks that make every night out unforgettable.
          </p>
        </div>

        {/* Already Premium */}
        {premiumStatus?.is_premium && (
          <Card className="glass p-6 border border-yellow-500/50 mb-8 fade-in">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">You&apos;re a Premium Member! 🎉</h3>
                <p className="text-slate-400">
                  Your subscription is active until{' '}
                  {new Date(premiumStatus.premium_expires_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card className="glass p-6 border border-slate-700 fade-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Ad-Free Experience</h3>
                <p className="text-sm text-slate-400">Browse bars without any interruptions or promotional content.</p>
              </div>
            </div>
          </Card>

          <Card className="glass p-6 border border-slate-700 fade-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Filter className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Advanced Filters</h3>
                <p className="text-sm text-slate-400">Filter bars by age range, music type, vibe, and more.</p>
              </div>
            </div>
          </Card>

          <Card className="glass p-6 border border-slate-700 fade-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">See Who&apos;s There</h3>
                <p className="text-sm text-slate-400">Preview the crowd before you arrive - see demographics in real-time.</p>
              </div>
            </div>
          </Card>

          <Card className="glass p-6 border border-slate-700 fade-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Exclusive Perks</h3>
                <p className="text-sm text-slate-400">VIP badge, priority support, and early access to new features.</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Pricing Cards */}
        {!premiumStatus?.is_premium && (
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {packages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`glass p-6 border fade-in ${
                  pkg.id === 'yearly'
                    ? 'border-yellow-500/50 ring-2 ring-yellow-500/30'
                    : 'border-slate-700'
                }`}
              >
                {pkg.id === 'yearly' && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold rounded-full">
                      BEST VALUE
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">{pkg.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-white">${pkg.amount}</span>
                    <span className="text-slate-400">/{pkg.id === 'yearly' ? 'year' : 'month'}</span>
                  </div>
                  {pkg.id === 'yearly' && (
                    <p className="text-green-400 text-sm mt-1">Save $19.89 per year!</p>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleCheckout(pkg.id)}
                  disabled={checkingOut !== null}
                  className={`w-full ${
                    pkg.id === 'yearly'
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {checkingOut === pkg.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Crown className="w-4 h-4 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>
              </Card>
            ))}
          </div>
        )}

        {/* FAQ */}
        <Card className="glass p-6 border border-slate-700 fade-in">
          <h3 className="text-xl font-bold text-white mb-4">Frequently Asked Questions</h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-white mb-1">Can I cancel anytime?</h4>
              <p className="text-sm text-slate-400">Yes! You can cancel your subscription at any time. You&apos;ll continue to have premium access until your current billing period ends.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-1">What payment methods do you accept?</h4>
              <p className="text-sm text-slate-400">We accept all major credit cards and debit cards through our secure payment processor, Stripe.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-1">Is my payment information secure?</h4>
              <p className="text-sm text-slate-400">Absolutely! We use Stripe for payment processing, which is PCI-compliant and uses bank-level encryption.</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Premium;
